import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export interface MentorshipFormData {
  id?: string;
  title: string;
  description: string;
  price: string;
  duration: string;
  gameId: string;
  coverImageUrl?: string | null;
  isActive: boolean;
  isGroup: boolean;
  maxParticipants: string;
}

interface MentorshipOfferingFormProps {
  initialData?: MentorshipFormData;
  onSubmit: (formData: MentorshipFormData, coverFile?: File | null) => Promise<void>;
  onCancel: () => void;
}

export const MentorshipOfferingForm: React.FC<MentorshipOfferingFormProps> = ({
  initialData,
  onSubmit,
  onCancel
}) => {
  const [formData, setFormData] = useState<MentorshipFormData>(
    initialData || {
      title: "",
      description: "",
      price: "",
      duration: "60",
      gameId: "",
      isActive: true,
      isGroup: false,
      maxParticipants: "1"
    }
  );
  
  const [games, setGames] = useState<Array<{ id: string, name: string }>>([]);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverPreview, setCoverPreview] = useState<string | null>(initialData?.coverImageUrl || null);
  const [isLoading, setIsLoading] = useState(false);
  
  const { toast } = useToast();
  
  // Fetch available games from the database
  useEffect(() => {
    const fetchGames = async () => {
      try {
        const { data, error } = await supabase
          .from("games")
          .select("id, name")
          .order("name");
          
        if (error) throw error;
        setGames(data || []);
      } catch (error) {
        console.error("Error fetching games:", error);
        toast({
          title: "Erro",
          description: "Não foi possível carregar os jogos disponíveis.",
          variant: "destructive"
        });
      }
    };
    
    fetchGames();
  }, [toast]);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  
  const handleSelectChange = (value: string, fieldName: string) => {
    setFormData({
      ...formData,
      [fieldName]: value,
    });
  };

  const handleSwitchChange = (checked: boolean, fieldName: string) => {
    setFormData({
      ...formData,
      [fieldName]: checked,
    });
  };
  
  const handleCoverImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setCoverFile(file);
      const objectUrl = URL.createObjectURL(file);
      setCoverPreview(objectUrl);
    }
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      await onSubmit(formData, coverFile);
    } catch (error) {
      console.error("Error submitting form:", error);
      toast({
        title: "Erro",
        description: "Ocorreu um erro ao salvar a mentoria.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid gap-2">
        <Label htmlFor="title" className="text-white">Título</Label>
        <Input
          id="title"
          name="title"
          placeholder="Ex: Treinamento Avançado de League of Legends"
          value={formData.title}
          onChange={handleInputChange}
          className="bg-[#242C37] border-[#2A3749] text-white"
          required
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="game" className="text-white">Jogo</Label>
        <Select 
          value={formData.gameId} 
          onValueChange={(value) => handleSelectChange(value, "gameId")}
        >
          <SelectTrigger className="w-full bg-[#242C37] border-[#2A3749] text-white">
            <SelectValue placeholder="Selecione um jogo" />
          </SelectTrigger>
          <SelectContent className="bg-[#242C37] border-[#2A3749] text-white">
            {games.map((game) => (
              <SelectItem key={game.id} value={game.id}>{game.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        <div className="grid gap-2">
          <Label htmlFor="duration" className="text-white">Duração (minutos)</Label>
          <Input
            id="duration"
            name="duration"
            type="number"
            placeholder="60"
            value={formData.duration}
            onChange={handleInputChange}
            className="bg-[#242C37] border-[#2A3749] text-white"
            required
          />
        </div>
        <div className="grid gap-2">
          <Label htmlFor="price" className="text-white">Preço (R$)</Label>
          <Input
            id="price"
            name="price"
            placeholder="100,00 (digite 0 para gratuito)"
            value={formData.price}
            onChange={handleInputChange}
            className="bg-[#242C37] border-[#2A3749] text-white"
          />
          <p className="text-xs text-gray-400">
            Digite 0 ou deixe vazio para tornar a mentoria gratuita
          </p>
        </div>
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="isGroup" className="text-white">Tipo de mentoria</Label>
        <div className="flex items-center justify-between bg-[#242C37] border border-[#2A3749] rounded-md p-3">
          <div>
            <p className="text-white font-medium">{formData.isGroup ? "Em grupo" : "Individual"}</p>
            <p className="text-sm text-gray-400">
              {formData.isGroup 
                ? "Múltiplos participantes por sessão" 
                : "Um único participante por sessão"}
            </p>
          </div>
          <Switch
            id="isGroup"
            checked={formData.isGroup}
            onCheckedChange={(checked) => handleSwitchChange(checked, "isGroup")}
          />
        </div>
      </div>
      
      {formData.isGroup && (
        <div className="grid gap-2 pl-3 border-l-2 border-[#2A3749]">
          <Label htmlFor="maxParticipants" className="text-white">Máximo de participantes</Label>
          <Input
            id="maxParticipants"
            name="maxParticipants"
            type="number"
            min="2"
            placeholder="5"
            value={formData.maxParticipants}
            onChange={handleInputChange}
            className="bg-[#242C37] border-[#2A3749] text-white w-32"
            required={formData.isGroup}
          />
          <p className="text-xs text-gray-400">
            Número máximo de participantes que podem se inscrever nesta mentoria em grupo
          </p>
        </div>
      )}
      
      <div className="grid gap-2">
        <Label htmlFor="description" className="text-white">Descrição</Label>
        <Textarea
          id="description"
          name="description"
          placeholder="Descreva detalhes sobre sua mentoria..."
          value={formData.description}
          onChange={handleInputChange}
          className="bg-[#242C37] border-[#2A3749] text-white resize-none min-h-[100px]"
          required
        />
      </div>
      
      <div className="grid gap-2">
        <Label htmlFor="coverImage" className="text-white">Imagem de Capa</Label>
        <div className="flex flex-col gap-3">
          <div className="bg-[#1A2233] border border-[#2D3748] rounded-lg p-4">
            <div className="flex items-start gap-2 mb-3">
              <div className="w-5 h-5 rounded-full bg-[#00B6F0] flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-black text-xs font-bold">i</span>
              </div>
              <div>
                <h4 className="text-[#00B6F0] font-semibold text-sm mb-2">Instruções para Imagem de Capa</h4>
                <ul className="text-gray-300 text-xs space-y-1">
                  <li>• <strong>Dimensões recomendadas:</strong> 800x480 pixels (proporção 5:3)</li>
                  <li>• <strong>Formatos aceitos:</strong> JPG, PNG, WebP</li>
                  <li>• <strong>Tamanho máximo:</strong> 5MB</li>
                  <li>• <strong>Qualidade:</strong> Use imagens nítidas e bem iluminadas</li>
                  <li>• <strong>Conteúdo:</strong> Evite texto pequeno, pois pode ficar ilegível</li>
                  <li>• <strong>Dica:</strong> A imagem será exibida com overlay escuro na parte inferior</li>
                </ul>
              </div>
            </div>
          </div>
          
          {coverPreview && (
            <div className="w-full h-32 overflow-hidden rounded-md border border-[#2A3749]">
              <img
                src={coverPreview}
                alt="Cover Preview"
                className="w-full h-full object-cover"
              />
            </div>
          )}
          <Input
            id="coverImage"
            name="coverImage"
            type="file"
            accept="image/jpeg,image/jpg,image/png,image/webp"
            onChange={handleCoverImageChange}
            className="bg-[#242C37] border-[#2A3749] text-white file:text-[#00B6F0] file:bg-[#223342] file:border-0"
          />
        </div>
      </div>
      
      <div className="grid gap-2">
        <div className="flex items-center gap-2">
          <Input
            id="isActive"
            name="isActive"
            type="checkbox"
            checked={formData.isActive}
            onChange={(e) => setFormData({...formData, isActive: e.target.checked})}
            className="w-4 h-4 bg-[#242C37] border-[#2A3749]"
          />
          <Label htmlFor="isActive" className="text-white">Ativo</Label>
        </div>
      </div>
      
      <div className="flex justify-end gap-3 mt-6">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel} 
          className="border-[#2A3749] text-white hover:bg-[#242C37]"
          disabled={isLoading}
        >
          Cancelar
        </Button>
        <Button 
          type="submit" 
          className="bg-[#00B6F0] text-black hover:bg-[#00A3D9]"
          disabled={isLoading}
        >
          {isLoading ? "Salvando..." : (initialData ? "Atualizar Mentoria" : "Criar Mentoria")}
        </Button>
      </div>
    </form>
  );
};
